import React from 'react';

/**
 * DonutChart - donut ring with a clean side legend.
 *
 * Radial text labels were replaced with a legend because tiny adjacent slices
 * (e.g. Local=2, Outstation=5) sit at almost the same angle and their labels
 * overlapped. A legend lists every category/value exactly once with no overlap
 * and lets the ring itself be drawn larger.
 */
const DonutChart = ({ data = [], size = 240, strokeWidth = 36, totalStudents = 0 }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const center = size / 2;

    // Total used only for arc proportions (may differ from totalStudents prop).
    const total = data.reduce((sum, item) => sum + (item.value || 0), 0);

    // Build arc segments starting from the top (12 o'clock), sweeping clockwise.
    let accumulatedAngle = 0;
    const segments = data.map((item) => {
        const percentage = total > 0 ? item.value / total : 0;
        const segmentLength = percentage * circumference;
        const offset = (accumulatedAngle / 360) * circumference; // start of this arc
        accumulatedAngle += percentage * 360;
        return { ...item, segmentLength, offset, percentage };
    });

    // Professional short label names for the legend.
    const getLabel = (label) => {
        if (label.includes('Inside')) return 'INSIDE CAMPUS';
        if (label.includes('Normal')) return 'OUTSIDE – REGULAR';
        if (label.includes('Local')) return 'OUTSIDE – LOCAL GP';
        if (label.includes('Outstation')) return 'OUTSIDE – OS GP';
        if (label.includes('Sem Break')) return 'OUTSIDE – SEM BREAK GP';
        if (label.includes('Bangalore')) return 'OUTSIDE – BANGALORE GP';
        return label.toUpperCase();
    };

    const innerHole = radius - strokeWidth / 2 - 4;

    return (
        <div className="donut-chart-wrap">
            <svg
                className="donut-chart-svg"
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                role="img"
                aria-label={`Total students ${totalStudents}`}
                style={{ flexShrink: 0 }}
            >
                {/* Background ring */}
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke="#eceff1"
                    strokeWidth={strokeWidth}
                />

                {/* Data segments */}
                {segments.map((seg, i) => (
                    seg.value > 0 && (
                        <circle
                            key={i}
                            cx={center}
                            cy={center}
                            r={radius}
                            fill="none"
                            stroke={seg.color}
                            strokeWidth={strokeWidth}
                            strokeDasharray={`${seg.segmentLength} ${circumference - seg.segmentLength}`}
                            strokeDashoffset={-seg.offset}
                            transform={`rotate(-90 ${center} ${center})`}
                        />
                    )
                ))}

                {/* Inner hole */}
                <circle cx={center} cy={center} r={innerHole} fill="#f7f8fa" />

                {/* Center total */}
                <text
                    x={center}
                    y={center - size * 0.03}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{
                        fontSize: size * 0.17,
                        fontWeight: 800,
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                        fill: '#1e1e2c',
                        letterSpacing: '-1px',
                    }}
                >
                    {totalStudents}
                </text>
                <text
                    x={center}
                    y={center + size * 0.11}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{
                        fontSize: size * 0.05,
                        fontWeight: 600,
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                        fill: '#6c757d',
                        letterSpacing: '1px',
                    }}
                >
                    TOTAL STUDENTS
                </text>
            </svg>

            {/* Legend — one row per category, no overlap possible */}
            <ul className="donut-legend">
                {segments
                    .filter((seg) => seg.value > 0)
                    .map((seg, i) => (
                        <li className="donut-legend-item" key={i}>
                            <span className="donut-legend-swatch" style={{ backgroundColor: seg.color }} />
                            <span className="donut-legend-label">{getLabel(seg.label)}</span>
                            <span className="donut-legend-value" style={{ color: seg.color }}>{seg.value}</span>
                        </li>
                    ))}
            </ul>
        </div>
    );
};

export default DonutChart;
